import { useIntl } from "react-intl";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useNotification } from "~hooks/useNotification.ts";
import type { StandingConfiguration } from "~types/standingConfiguration";
import { useBuildStandings } from "./useBuildStandings.ts";
import { useCreateStanding } from "./useCreateStanding.ts";
import { useDeleteStanding } from "./useDeleteStanding.ts";
import { useDeleteStandings } from "./useDeleteStandings.ts";

export const useStandingsService = () => {
  const { mutateAsync: buildStandingsFn, isPending: isBuildPending } = useBuildStandings();
  const { mutateAsync: createStandingFn, isPending: isCreatePending } = useCreateStanding();
  const { mutateAsync: deleteStandingFn, isPending: isDeleteStandingPending } = useDeleteStanding();
  const { mutateAsync: deleteStandingsFn, isPending: isDeleteStandingsPending } = useDeleteStandings();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  const { create, remove } = useApiOperations(ApiOperationItems.STANDING);

  const buildStandings = async (standingConfiguration?: StandingConfiguration) => {
    if (!standingConfiguration?.standingConfigurationId) {
      notification.error({ message: formatMessage({ id: "standings.build.error.standingsId" }) });
      return undefined;
    }

    return create(() => buildStandingsFn(), { successMessageId: "standings.build.success" });
  };

  // will be typed after refactoring of create form
  const createStanding = (body: unknown) => create(() => createStandingFn(body));

  const deleteStanding = async (standingId: string) => Boolean(await remove(() => deleteStandingFn(standingId)));

  const deleteStandings = async () => Boolean(await remove(() => deleteStandingsFn(), { successMessageId: "standings.delete.success" }));

  return {
    buildStandings,
    isBuildPending,
    createStanding,
    isCreatePending,
    deleteStanding,
    isDeleteStandingPending,
    deleteStandings,
    isDeleteStandingsPending,
  };
};
